import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { StatusBadge } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { DrawerSection, KeyValue } from "./DetailDrawer.tsx";
import type { Interaction } from "./types.ts";

/**
 * The detail shown in the right-side drawer for one interaction (modelled on the AgentDNA
 * dashboard's InteractionDetail): a header with the id and threat state, then Flow (initiator →
 * target), Metadata (ids, time, intent, threat), an Audit trail timeline, and the raw record. Pure
 * presentation over the already-fetched row — no extra fetch.
 */
export function InteractionDetail(props: {
  readonly interaction: Interaction;
  readonly onClose: () => void;
  readonly titleId: string;
}): ReactNode {
  const i = props.interaction;
  const selfInteraction = i.initiator.id === i.target.id;

  return (
    <>
      <header className="drawer-head">
        <div className="drawer-head__title">
          <h2 id={props.titleId} className="drawer-head__heading">
            Interaction
          </h2>
          <span className="drawer-head__id">{i.id}</span>
        </div>
        {i.threat ? (
          <StatusBadge level="error" label="Threat" size="sm" />
        ) : (
          <StatusBadge level="healthy" label="Safe" size="sm" />
        )}
        <button type="button" className="drawer-head__close" onClick={props.onClose} aria-label="Close details">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="drawer-body">
        <DrawerSection title="Flow">
          {selfInteraction ? (
            <div className="drawer-flow">
              <div className="drawer-flow__node">
                <span className="drawer-flow__role">Initiator &amp; target</span>
                <span className="drawer-flow__name">{i.initiator.name}</span>
              </div>
            </div>
          ) : (
            <div className="drawer-flow">
              <div className="drawer-flow__node">
                <span className="drawer-flow__role">Initiator</span>
                <span className="drawer-flow__name">{i.initiator.name}</span>
              </div>
              <div className="drawer-flow__arrow" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M6 13l6 6 6-6" />
                </svg>
              </div>
              <div className="drawer-flow__node">
                <span className="drawer-flow__role">
                  Target · {i.targetType === "tool" ? "app" : i.targetType}
                </span>
                <span className="drawer-flow__name">{i.target.name}</span>
              </div>
            </div>
          )}
        </DrawerSection>

        <DrawerSection title="Metadata">
          <KeyValue
            rows={[
              { k: "Interaction ID", v: <code className="drawer-mono">{i.id}</code> },
              {
                k: "Time",
                v: <time dateTime={isoAttr(i.created)}>{formatDateTime(i.created)}</time>,
              },
              { k: "Runtime", v: `${i.runtime} ms` },
              ...(i.blockType !== undefined
                ? [{ k: "Block type", v: <code className="drawer-mono">{i.blockType}</code> }]
                : []),
              {
                k: "Threat detected",
                v: (
                  <span className={i.threat ? "drawer-threat" : "drawer-safe"}>
                    {i.threat ? "true" : "false"}
                  </span>
                ),
              },
              {
                k: "Intent",
                v: (
                  <Link
                    className="drawer-link"
                    to={`/intents/${encodeURIComponent(i.intent.id)}`}
                    onClick={props.onClose}
                  >
                    {i.intent.name}
                  </Link>
                ),
              },
              { k: "Intent ID", v: <code className="drawer-mono">{i.intent.id}</code> },
            ]}
          />
        </DrawerSection>

        <DrawerSection title="Audit trail">
          <ol className="drawer-timeline">
            <li className="drawer-timeline__item">
              <span className="drawer-timeline__dot" aria-hidden="true" />
              <div>
                <div className="drawer-timeline__name">Identity authorized</div>
                <div className="drawer-timeline__desc">
                  Signature {i.initiator.id.slice(-6)} matched against a registered key.
                </div>
              </div>
            </li>
            <li className="drawer-timeline__item">
              <span className="drawer-timeline__dot" aria-hidden="true" />
              <div>
                <div className="drawer-timeline__name">Access verified</div>
                <div className="drawer-timeline__desc">
                  Policies checked for {i.target.name}.
                </div>
              </div>
            </li>
            {i.threat && (
              <li className="drawer-timeline__item drawer-timeline__item--threat">
                <span className="drawer-timeline__dot" aria-hidden="true" />
                <div>
                  <div className="drawer-timeline__name">Threat detected</div>
                  <div className="drawer-timeline__desc">Interaction flagged for review.</div>
                </div>
              </li>
            )}
            <li className="drawer-timeline__item">
              <span className="drawer-timeline__dot" aria-hidden="true" />
              <div>
                <div className="drawer-timeline__name">Envelope sealed</div>
                <div className="drawer-timeline__desc">Interaction recorded, signed and committed.</div>
              </div>
            </li>
          </ol>
        </DrawerSection>

        <DrawerSection title="Raw data">
          <pre className="drawer-raw">{JSON.stringify(i, null, 2)}</pre>
        </DrawerSection>
      </div>
    </>
  );
}
